/**
 * Touch-typing curriculum. Each lesson introduces a few keys and drills them with
 * repeated patterns before mixing in words. Lines are typed one after another, like a
 * solo race with no opponents.
 */
export interface Lesson {
  id: string;
  title: string;
  description: string;
  /** Keys introduced in this lesson (for the on-screen keyboard highlight). */
  keys: string;
  lines: string[];
  /** WPM needed for the third star. */
  targetWpm: number;
  /** Meaning of each line, shown under the text (language lessons). */
  meanings?: string[];
  /** Accept unaccented letters for accented ones. */
  accentInsensitive?: boolean;
}

export type Finger = 'LP' | 'LR' | 'LM' | 'LI' | 'RI' | 'RM' | 'RR' | 'RP' | 'T';

export const FINGER_NAMES: Record<Finger, string> = {
  LP: 'left pinky',
  LR: 'left ring finger',
  LM: 'left middle finger',
  LI: 'left index finger',
  RI: 'right index finger',
  RM: 'right middle finger',
  RR: 'right ring finger',
  RP: 'right pinky',
  T: 'thumb',
};

const FINGER_KEYS: Record<Finger, string> = {
  LP: '`1qaz~!QAZ',
  LR: '2wsx@WSX',
  LM: '3edc#EDC',
  LI: '4rfv5tgb$RFV%TGB',
  RI: '6yhn7ujm^YHN&UJM',
  RM: '8ik,*IK<',
  RR: '9ol.(OL>',
  RP: "0p;/-=[]\\')P:?_+{}|\"",
  T: ' ',
};

const FINGER_OF = new Map<string, Finger>();
for (const [finger, keys] of Object.entries(FINGER_KEYS) as [Finger, string][]) {
  for (const k of keys) FINGER_OF.set(k, finger);
}

export function fingerFor(ch: string): Finger | null {
  return FINGER_OF.get(ch) ?? null;
}

/** Whether a character needs Shift, and which hand should press it (opposite to the letter). */
export function shiftFor(ch: string): 'L' | 'R' | null {
  if (ch.length !== 1) return null;
  const needsShift = /[A-Z~!@#$%^&*()_+{}|:"<>?]/.test(ch);
  if (!needsShift) return null;
  const f = fingerFor(ch);
  if (!f || f === 'T') return null;
  return f.startsWith('L') ? 'R' : 'L';
}

export const LESSONS: Lesson[] = [
  {
    id: 'home-1',
    title: 'Home row: F and J',
    description: 'Find the bumps on F and J. Index fingers rest there; the other fingers line up beside them.',
    keys: 'fj',
    targetWpm: 15,
    lines: ['fff jjj fff jjj', 'fj fj jf jf fjf jfj', 'ff jj fj jf fjj jff', 'jjf ffj jfj fjf jf fj', 'f j fj jf fjfj jfjf'],
  },
  {
    id: 'home-2',
    title: 'Home row: D K S L',
    description: 'Middle and ring fingers. Keep your index fingers anchored on F and J while you reach.',
    keys: 'dksl',
    targetWpm: 15,
    lines: ['ddd kkk sss lll', 'dk dk sl sl kd kd ls ls', 'fds jkl fds jkl', 'sdf lkj sdf lkj', 'dfs klj skd lfj', 'fdsjkl fdsjkl sldkfj'],
  },
  {
    id: 'home-3',
    title: 'Home row: A and ;',
    description: 'Pinky fingers. They are weak at first; this lesson is short on purpose.',
    keys: 'a;',
    targetWpm: 18,
    lines: ['aaa ;;; aaa ;;;', 'a; a; ;a ;a a;a ;a;', 'asdf jkl; asdf jkl;', ';lkj fdsa ;lkj fdsa', 'a sad lad; all fall;', 'dad had a salad; a lass asks'],
  },
  {
    id: 'home-4',
    title: 'Home row: G and H',
    description: 'Index fingers stretch inwards for G and H, then return to F and J.',
    keys: 'gh',
    targetWpm: 20,
    lines: ['fgf jhj fgf jhj', 'gh hg gh hg ghg hgh', 'glad flash hash slash', 'half a glass; a shag; dash', 'add gas; hall flags; sad lads', 'a glad lad has a flask; ask dad'],
  },
  {
    id: 'top-1',
    title: 'Top row: E I R U',
    description: 'Reach up from the home row and come straight back down. Do not move your wrist.',
    keys: 'eiru',
    targetWpm: 20,
    lines: ['ded kik ded kik', 'frf juj frf juj', 'ei ie ru ur eiru urie', 'fire ride rude sure lure', 'here is a fire; a huge rug', 'ask her; his idea; sail flies'],
  },
  {
    id: 'top-2',
    title: 'Top row: T Y W O',
    description: 'Index fingers reach for T and Y; ring fingers take W and O.',
    keys: 'tywo',
    targetWpm: 22,
    lines: ['ftf jyj sws lol', 'ty yt wo ow tywo owyt', 'two toy tow yet wet', 'the tree is tall; try it', 'lots of hot toast; we grow', 'you wait; they roll it out'],
  },
  {
    id: 'top-3',
    title: 'Top row: Q and P',
    description: 'Pinkies again. Q is above A, P is above ;.',
    keys: 'qp',
    targetWpm: 22,
    lines: ['aqa ;p; aqa ;p;', 'qp pq qap pq; qpq', 'quit pity port quip', 'the quiet pup sleeps; equip it', 'a quip; a top prop; quite a pair', 'people who quit rarely prosper'],
  },
  {
    id: 'bottom-1',
    title: 'Bottom row: C V M ,',
    description: 'Reach down and back. Middle fingers on C and comma, index fingers on V and M.',
    keys: 'cvm,',
    targetWpm: 22,
    lines: ['dcd fvf jmj k,k', 'cv vc m, ,m cvm, ,mvc', 'cave move come calm', 'move it, calm cats, comic rice', 'a mime came, a camel moved', 'come home, mum; give me five'],
  },
  {
    id: 'bottom-2',
    title: 'Bottom row: X Z B N .',
    description: 'The last letters. Ring finger on X, pinky on Z, index fingers on B and N.',
    keys: 'xzbn.',
    targetWpm: 24,
    lines: ['sxs aza fbf jnj l.l', 'xz bn zx nb xzbn.', 'zebra box next bend zone.', 'the fox ran by. buzz. jazz.', 'six boxes. one banana. nine bins.', 'never zoom by an open box.'],
  },
  {
    id: 'capitals',
    title: 'Capitals with Shift',
    description: 'Use the Shift key on the opposite hand from the letter, so the typing hand stays on the home row.',
    keys: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    targetWpm: 25,
    lines: ['Fj Jf Dk Kd Sl Ls A; ;A', 'The Cat. The Dog. The Fox.', 'Anna and Bob met Carl in Dover.', 'Every Friday George hikes.', 'Jump Kick Leap Move Nudge', 'Sam Tells Una: Very Well, Xander.'],
  },
  {
    id: 'numbers',
    title: 'Number row',
    description: 'Each finger reaches up two rows. 1 2 3 4 5 for the left hand, 6 7 8 9 0 for the right.',
    keys: '1234567890',
    targetWpm: 20,
    lines: ['a1a s2s d3d f4f f5f', 'j6j j7j k8k l9l ;0;', '12 34 56 78 90 09 87', 'room 101; bus 42; year 2026', 'call 555 0192 at 8 or 9', '3 cats, 14 dogs and 250 fish'],
  },
  {
    id: 'punctuation',
    title: 'Punctuation',
    description: "Apostrophes, question marks, exclamation marks and dashes. Right pinky does most of the work.",
    keys: "'?!-:\"",
    targetWpm: 25,
    lines: [";'; ;'; ;/; ;?;", "it's don't we'll they're", 'Really? Yes! Go on - now.', '"Wait," she said. "Why?"', "It's late - isn't it? Let's go!", 'Note: this works; that does not.'],
  },
  {
    id: 'words-1',
    title: 'Common words',
    description: 'The most frequent English words. Type them as single motions rather than letter by letter.',
    keys: '',
    targetWpm: 30,
    lines: ['the and to of a in is it you that', 'he was for on are with as his they', 'be at one have this from or had by', 'not word but what some we can out other', 'were all there when up use your how said', 'an each she which do their time if will way'],
  },
  {
    id: 'words-2',
    title: 'Word pairs and phrases',
    description: 'Short phrases build rhythm across the space bar. Keep the beat steady.',
    keys: '',
    targetWpm: 32,
    lines: ['keep going; you can do it', 'one more time, a little faster', 'read ahead and stay relaxed', 'small steps add up over time', 'trust your fingers, watch the screen', 'steady rhythm beats sudden bursts'],
  },
  {
    id: 'sentences',
    title: 'Full sentences',
    description: 'Everything together: capitals, punctuation and long words. This is race pace.',
    keys: '',
    targetWpm: 35,
    lines: [
      'Every expert was once a beginner who refused to give up.',
      'Focus on the next step, not the whole staircase.',
      "You do not have to be perfect, you just have to keep going.",
      'Consistency will take you further than motivation ever could.',
      'Type the word you see, not the word you expect.',
    ],
  },
];

export function lessonById(id: string): Lesson | undefined {
  return LESSONS.find((l) => l.id === id);
}

/** Stars for a completed lesson: 1 for finishing, 2 for accuracy, 3 for speed too. */
export function lessonStars(lesson: Lesson, wpm: number, accuracy: number): 1 | 2 | 3 {
  if (accuracy >= 0.97 && wpm >= lesson.targetWpm) return 3;
  if (accuracy >= 0.95) return 2;
  return 1;
}
